import {
  FocusEvent,
  ReactNode,
  Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import {
  Box,
  ButtonBase,
  Divider,
  InputAdornment,
  InputBase,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  BillingInterval,
  CreateTeamErrors,
  CreateTeamFormData,
  MAX_TEAM_SEATS,
  MIN_TEAM_SEATS,
  computeTotal,
  formatMoneyShort,
} from "../types";
import { LabelValueRow as Row } from "../../components/LabelValueRow";
import {
  MONTHLY_PRO_CREDITS,
  ANNUAL_PRO_CREDITS,
} from "../../../constants/proCredits";

const ACCENT = "#7A4FE0";
const MUTED = "#62626B";
const BORDER = "#E3E3E8";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#fff" },
};

const clampSeats = (n: number) =>
  Math.max(MIN_TEAM_SEATS, Math.min(MAX_TEAM_SEATS, n));

const FieldLabel = ({
  htmlFor,
  id,
  children,
  hint,
}: {
  htmlFor?: string;
  id?: string;
  children: ReactNode;
  hint?: ReactNode;
}) => (
  <Stack direction="row" alignItems="baseline" spacing={1}>
    <Typography
      component={htmlFor ? "label" : "span"}
      htmlFor={htmlFor}
      id={id}
      sx={{ fontSize: 14, fontWeight: 600 }}
    >
      {children}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 13, color: "#8E8E97" }}>{hint}</Typography>
    )}
  </Stack>
);

// What's typed is what's quoted: values above the max snap to it as you type,
// values below the min stay (the step shows the error and Continue is disabled),
// and only an emptied box keeps a draft, restored to the last count on blur.
const SeatStepper = ({
  seats,
  onSeats,
  errorId,
  onLeave,
}: {
  seats: number;
  onSeats: (n: number) => void;
  /** Id of the visible seat error, when there is one. */
  errorId?: string;
  /** The box lost focus: its error (if any) may show from now on. */
  onLeave?: () => void;
}) => {
  const [draft, setDraft] = useState(String(seats));
  useEffect(() => setDraft(String(seats)), [seats]);

  const commit = () => {
    if (draft === "") setDraft(String(seats));
  };

  const stepSx = {
    width: 44,
    fontSize: 20,
    color: "#16161A",
    "&:hover": { bgcolor: "#F5F5F7" },
    "&.Mui-disabled": { opacity: 0.3 },
  };

  return (
    <Stack
      direction="row"
      sx={{
        flex: "none",
        height: 44,
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <ButtonBase
        aria-label="Remove a seat"
        disabled={seats <= MIN_TEAM_SEATS}
        onClick={() => onSeats(clampSeats(seats - 1))}
        sx={stepSx}
      >
        −
      </ButtonBase>
      <InputBase
        value={draft}
        onChange={(e) => {
          const v = e.target.value.trim();
          // Whole numbers only: a paste like "2.5" is ignored, never read as 25.
          if (!/^\d*$/.test(v)) return;
          if (v === "") {
            setDraft("");
            return;
          }
          const n = Math.min(Number.parseInt(v, 10), MAX_TEAM_SEATS);
          setDraft(String(n));
          onSeats(n);
        }}
        onBlur={() => {
          commit();
          onLeave?.();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            onLeave?.();
          }
        }}
        inputProps={{
          "aria-label": "Number of seats",
          "aria-invalid": Boolean(errorId),
          "aria-describedby": errorId,
          inputMode: "numeric",
          "data-testid": "create-team-seats",
          style: { textAlign: "center", padding: 0 },
        }}
        sx={{
          width: 52,
          borderLeft: `1px solid ${BORDER}`,
          borderRight: `1px solid ${BORDER}`,
          fontSize: 16,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      />
      <ButtonBase
        aria-label="Add a seat"
        disabled={seats >= MAX_TEAM_SEATS}
        onClick={() => onSeats(clampSeats(seats + 1))}
        sx={stepSx}
      >
        +
      </ButtonBase>
    </Stack>
  );
};

const PlanCard = ({
  interval,
  selected,
  title,
  price,
  per,
  credits,
  badge,
}: {
  interval: BillingInterval;
  selected: boolean;
  title: string;
  /** Undefined while prices load: a placeholder shows instead. */
  price?: string;
  per: string;
  credits: string;
  badge?: string;
}) => (
  <Stack
    component="label"
    spacing={0.5}
    data-testid={`create-team-interval-${interval}`}
    sx={{
      p: "12px 16px",
      borderRadius: "14px",
      border: `1.5px solid ${selected ? ACCENT : BORDER}`,
      bgcolor: selected ? "#FAF8FF" : "#fff",
      boxShadow: selected ? "0 0 0 3px rgba(122,79,224,.12)" : "none",
      cursor: "pointer",
      transition: "border-color 120ms ease, background-color 120ms ease",
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      sx={{ minHeight: 22, columnGap: 1.25, rowGap: 0.5 }}
    >
      <Radio
        value={interval}
        size="small"
        inputProps={{ "aria-label": `${title} plan` }}
        sx={{ p: 0, color: "#C9C9D1", "&.Mui-checked": { color: ACCENT } }}
      />
      <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography>
      {badge && (
        <Box
          component="span"
          sx={{
            ml: "auto",
            fontSize: 12,
            fontWeight: 600,
            color: "#5A35C4",
            bgcolor: "#ECE5FC",
            px: 1,
            py: "3px",
            borderRadius: "6px",
            whiteSpace: "nowrap",
          }}
        >
          {badge}
        </Box>
      )}
    </Stack>
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={0.75}
      sx={{ pl: "30px" }}
    >
      <Typography
        sx={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em" }}
      >
        {price ?? <Skeleton width={72} />}
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: MUTED }}>
        / seat / {per}
      </Typography>
    </Stack>
    <Typography sx={{ pl: "30px", fontSize: 13, color: MUTED }}>
      {credits}
    </Typography>
  </Stack>
);

interface Props {
  data: CreateTeamFormData;
  errors: CreateTeamErrors;
  onChange: (patch: Partial<CreateTeamFormData>) => void;
  /** Marks a field as touched (on blur) so its error may surface. */
  onBlur: (field: keyof CreateTeamErrors) => void;
  monthlyPerSeat: number;
  yearlyPerSeat: number;
  currency: string;
  /** False until real Stripe prices are in; amounts show as placeholders until then. */
  pricesLoaded: boolean;
  /** Lets the wizard focus (and scroll to) an invalid field on Continue. */
  nameInputRef?: Ref<HTMLInputElement>;
  domainInputRef?: Ref<HTMLInputElement>;
}

/** Team name, company website, seat count and billing plan, with a live total. */
export const TeamDetailsStep = ({
  data,
  errors,
  onChange,
  onBlur,
  monthlyPerSeat,
  yearlyPerSeat,
  currency,
  pricesLoaded,
  nameInputRef,
  domainInputRef,
}: Props) => {
  const nameId = useId();
  const domainId = useId();
  const billingLabelId = useId();
  const seatErrorId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  // Focus pulled out of the modal (e.g. the page behind it autofocusing its search
  // box as it mounts) isn't the user leaving the field: no error yet. Focus moving to
  // the modal's own surface (a click on blank space) still counts.
  const handleBlur = (field: keyof CreateTeamErrors) => (e: FocusEvent) => {
    const to = e.relatedTarget as Node | null;
    const scope = e.currentTarget.closest(".MuiModal-root") ?? rootRef.current;
    if (to && !scope?.contains(to)) return;
    onBlur(field);
  };

  const yearly = data.interval === "yearly";
  const pricePerSeat = yearly ? yearlyPerSeat : monthlyPerSeat;
  const total = formatMoneyShort(
    computeTotal(data.seats, pricePerSeat),
    currency,
  );
  const savingPerSeat = monthlyPerSeat * 12 - yearlyPerSeat;
  const creditsPerSeat = yearly ? ANNUAL_PRO_CREDITS : MONTHLY_PRO_CREDITS;

  return (
    <Stack ref={rootRef}>
      <Stack spacing={0.75}>
        <FieldLabel htmlFor={nameId}>Team name</FieldLabel>
        <TextField
          id={nameId}
          value={data.firmName}
          onChange={(e) => onChange({ firmName: e.target.value })}
          onBlur={handleBlur("firmName")}
          error={!!errors.firmName}
          helperText={errors.firmName}
          placeholder="e.g. Atelier Nord"
          size="small"
          fullWidth
          autoFocus
          inputRef={nameInputRef}
          sx={fieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" aria-hidden>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "5px",
                    background: "linear-gradient(135deg,#8A8A96,#2E2E36)",
                  }}
                />
              </InputAdornment>
            ),
          }}
          inputProps={{ "data-testid": "create-team-firmName" }}
        />
      </Stack>

      <Stack spacing={0.75} sx={{ mt: 2 }}>
        <FieldLabel htmlFor={domainId}>Company website</FieldLabel>
        <TextField
          id={domainId}
          size="small"
          value={data.domain}
          onChange={(e) => onChange({ domain: e.target.value })}
          onBlur={handleBlur("domain")}
          error={!!errors.domain}
          helperText={
            errors.domain ??
            "Only people with an email at this domain can be invited."
          }
          placeholder="acme.com"
          fullWidth
          inputRef={domainInputRef}
          sx={fieldSx}
          inputProps={{ "data-testid": "create-team-domain" }}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mt: 2.5 }}
      >
        <Stack spacing={0.5}>
          <FieldLabel>Seats</FieldLabel>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            Includes you. Invite people and set roles after payment.
          </Typography>
        </Stack>
        <SeatStepper
          seats={data.seats}
          onSeats={(seats) => onChange({ seats })}
          onLeave={() => onBlur("seats")}
          errorId={errors.seats ? seatErrorId : undefined}
        />
      </Stack>
      {errors.seats && (
        <Typography
          id={seatErrorId}
          variant="body2"
          color="error"
          sx={{ mt: 1 }}
        >
          {errors.seats}
        </Typography>
      )}

      <Stack spacing={1} sx={{ mt: 2.5 }}>
        <FieldLabel id={billingLabelId}>Billing</FieldLabel>
        <RadioGroup
          aria-labelledby={billingLabelId}
          value={data.interval}
          onChange={(e) =>
            onChange({ interval: e.target.value as BillingInterval })
          }
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1.25,
          }}
        >
          <PlanCard
            interval="yearly"
            selected={yearly}
            title="Yearly"
            price={
              pricesLoaded
                ? formatMoneyShort(yearlyPerSeat, currency)
                : undefined
            }
            per="year"
            credits={`${ANNUAL_PRO_CREDITS} credits per seat, upfront`}
            badge={
              pricesLoaded && savingPerSeat > 0
                ? `Save ${formatMoneyShort(savingPerSeat, currency)} / seat`
                : undefined
            }
          />
          <PlanCard
            interval="monthly"
            selected={!yearly}
            title="Monthly"
            price={
              pricesLoaded
                ? formatMoneyShort(monthlyPerSeat, currency)
                : undefined
            }
            per="month"
            credits={`${MONTHLY_PRO_CREDITS} credits per seat, every month`}
          />
        </RadioGroup>
      </Stack>

      {!pricesLoaded ? (
        <Skeleton
          variant="rounded"
          height={120}
          data-testid="create-team-summary-loading"
          sx={{ mt: 1.5, borderRadius: "14px" }}
        />
      ) : (
        <Stack
          spacing={1}
          sx={{
            mt: 1.5,
            p: "12px 16px",
            borderRadius: "14px",
            bgcolor: "#F7F7F9",
          }}
        >
          <Row
            label={`${data.seats} ${data.seats === 1 ? "seat" : "seats"} × ${formatMoneyShort(pricePerSeat, currency)} / ${yearly ? "year" : "month"}`}
            value={total}
          />
          <Row
            label="Team credit pool"
            value={`${(data.seats * creditsPerSeat).toLocaleString("en-US")} credits ${yearly ? "upfront" : "per month"}`}
          />
          {yearly && savingPerSeat > 0 && (
            <Row
              label="Saving vs monthly"
              value={`${formatMoneyShort(data.seats * savingPerSeat, currency)} / year`}
              valueColor="#2F7D4F"
            />
          )}
          <Divider sx={{ borderColor: "#E6E6EB" }} />
          <Row
            label={`Total, billed ${yearly ? "yearly" : "monthly"}`}
            value={`${total} / ${yearly ? "yr" : "mo"}`}
            emphasis
          />
        </Stack>
      )}
    </Stack>
  );
};
